import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  DAILY_BUDGET,
  IMPRESSIONS,
  Metric,
  MIN_DAILY_SPEND,
  MONTHLY_BUDGET,
  ROAS,
  TARGET_ACOS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AuthService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { mergeAdStatsWithTargetHistory, sumAdStats } from "@front/m19-utils";
import { ChartData, DataSetConfiguration } from "@m19-board/shared/chart-modal/chart-modal.component";
import { ChartRendererComponent } from "@m19-board/shared/chart-modal/chart-renderer.component";
import { combineLatest, combineLatestWith, map, Observable, switchMap } from "rxjs";

@Component({
  selector: "app-business-stats",
  imports: [ChartRendererComponent],
  templateUrl: "./business-stats.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessStatsComponent {
  private readonly statsService = inject(StatsService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);

  readonly strategy = input.required<StrategyEx>();
  strategy$ = toObservable(this.strategy);

  readonly METRIC_COLUMNS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];

  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.businessStats };

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly selectedMetrics = [AD_SALES, COST];

  readonly dataSetConfiguration: DataSetConfiguration<AdStatsEx> = {
    aggregationFunction: [mergeAdStatsWithTargetHistory],
    metricsOnSameScale: [
      [AD_SALES, COST],
      [ACOS, TARGET_ACOS],
      [COST, MIN_DAILY_SPEND, DAILY_BUDGET, MONTHLY_BUDGET],
    ],
  };

  private readonly dailyBusinessStats$ = combineLatest([
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.dateRange$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    switchMap(([am, dr, currency]) =>
      this.statsService.getDailyBusinessStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
    ),
    combineLatestWith(this.strategy$),
    map(([data, strategy]) => data.filter((d) => d.strategyId === strategy.strategyId)),
  );

  readonly chartData$: Observable<ChartData<AdStatsEx>> = this.dailyBusinessStats$.pipe(
    map((data) => {
      const total = data.reduce((acc, curr) => sumAdStats(acc, curr), {});
      return {
        data,
        totalData: total,
      };
    }),
  );
}
